import { Box, Button, Divider, IconButton, Menu, MenuItem, TextField, Toolbar } from '@mui/material';
import FilterNoneIcon from "@mui/icons-material/FilterNone";
import BorderOuterIcon from "@mui/icons-material/BorderOuter";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import GridOnIcon from "@mui/icons-material/GridOn";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import LabelOffIcon from '@mui/icons-material/LabelOff';
import LabelIcon from '@mui/icons-material/Label';
import CloseIcon from '@mui/icons-material/Close';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircleOutlined';
import React, { useEffect, useRef, useState } from 'react';
import { useDesignStore } from 'context/DesignContext';
import { scheduleAutosave } from 'components/Viewer3d/SaveManager';
import SaveTemplatePanel from 'components/Modals/SaveTemplateModal/SaveTemplatePanel';
import EngravingIcon from '@mui/icons-material/FormatShapes';
import AutoDesignIcon from '@mui/icons-material/Star';
import SurfaceEditIcon from '@mui/icons-material/Edit';
import AdditionIcon from '@mui/icons-material/ControlCamera';
import SettingsIcon from '@mui/icons-material/Checklist';
import SchoolIcon from '@mui/icons-material/School';
import { requestTutorial } from 'Tutorial/tutorialStore';
import { useViewerStore, ViewerTabsMode } from 'context/ViewerContext';
import { MeasurementSystem } from 'components/Viewer3d/consts';
import { useAuth } from 'context/AuthContext';
import { canUseDevelopmentUi } from 'auth/permissions';


const MobileTopBar: React.FC<{ onMenuClick: () => void, onPropertyPanelTabClick: (tab: ViewerTabsMode | undefined) => void }> = ({ onMenuClick, onPropertyPanelTabClick }) => {

   const [localDesignName, setLocalDesignName] = useState('');
   const [isViewMenuOpen, setViewMenuOpen] = useState(false);
   const [isEditMenuOpen, setEditMenuOpen] = useState(false);
   const [isSaveTemplatePanelOpen, setSaveTemplatePanelOpen] = useState(false);

   const designNameElRef = useRef<HTMLInputElement>(null);
   const viewMenuButtonElRef = useRef<HTMLButtonElement>(null);
   const editMenuButtonElRef = useRef<HTMLButtonElement>(null);
   
   const setDesignName = useDesignStore((state) => state.setDesignName);
   const designLoaded = useDesignStore((state) => state.designLoaded);
   const insoleDesign = useDesignStore((state) => state.insoleDesign);
   const liveModeActive = useViewerStore((state) => state.liveMode);
   const AlignPressureActive = useViewerStore((state) => state.alignPressure);
   const { user, isExhibition } = useAuth();
   const canUseChatbox = canUseDevelopmentUi(user, isExhibition);

   const handleTutorialClick = () => {
      // Close the menu first: the walkthrough opens it again itself when it
      // reaches the view controls, and a menu left open would cover the icons
      // it is about to point at.
      setViewMenuOpen(false);
      requestTutorial();
   }

   const handleCloseViewMenu = () => {
      setViewMenuOpen(false);
   }

   const handleOpenViewMenu = () => {
      setViewMenuOpen(true);
   }

   const handleCloseEditMenu = () => {
      setEditMenuOpen(false);
      onPropertyPanelTabClick(undefined);
   }

   const handleOpenEditMenu = () => {
      setEditMenuOpen(true);
   }

   const handleBlur = (e: React.SyntheticEvent<HTMLElement>) => {
      const designNameElText = (e.currentTarget.textContent || '').trim();
      if (designNameElText === '') {
         const previousName = insoleDesign?.name || '';
         setLocalDesignName(previousName);
         if (designNameElRef.current) {
            designNameElRef.current.textContent = previousName;
         }
         console.warn('Empty input is not allowed for design title. Reverted.');
         return;
      }
      if (designNameElText !== insoleDesign?.name) {
         setLocalDesignName(designNameElText);
         setDesignName(designNameElText);
         scheduleAutosave('New design title', { updateDesignInfo: true, now: true });
      }
   };

   const handleSaveTemplateClose = () => {
      setSaveTemplatePanelOpen(false);
   };

   useEffect(() => {
      if (designLoaded == false || insoleDesign === null) return;
      setLocalDesignName(insoleDesign.name);
   }, [designLoaded]);

   useEffect(() => {
      const handleResize = () => {
         if (insoleDesign === null) return;
         setLocalDesignName(insoleDesign.name);
      };

      handleResize();
      window.addEventListener('resize', handleResize);

      return () => {
      window.removeEventListener('resize', handleResize);
      };
   }, []);

   const handlePropertyPanelTabClick = (tab: ViewerTabsMode) => {
      onPropertyPanelTabClick(tab);
      setEditMenuOpen(false);
   }

   const handleToggleColoringClick = () => {
      useViewerStore.getState().toggleColoring();
      setViewMenuOpen(false);
   }

   const handleToggleCameraClick = () => {
      const viewerState = useViewerStore.getState();
      viewerState.setPerspectiveCamera(!viewerState.perspectiveCamera);
      setViewMenuOpen(false);
   }

   const handleCycleWireFrameClick = () => {
      useViewerStore.getState().cycleWireframe();
      setViewMenuOpen(false);
   }

   const handleToggleClippingClick = () => {
      useViewerStore.getState().toggleClipping();
      setViewMenuOpen(false);
   }

   const handleToggleGridsClick = () => {
      useViewerStore.getState().toggleGrids();
      setViewMenuOpen(false);
   }

   const handleCycleFeetViewClick = () => {
      useViewerStore.getState().cycleFeetView();
      setViewMenuOpen(false);
   }

   const handleToggleLiveModeClick = () => {
      useViewerStore.getState().setOldLiveMode(useViewerStore.getState().liveMode);
      useViewerStore.getState().toggleLiveMode();
      setViewMenuOpen(false);
   }

   const handleDisableActiveActionClick = () => {
      useViewerStore.getState().setLiveMode(useViewerStore.getState().oldLiveMode);
      useViewerStore.getState().toggleAlignPressure();
   }

   const handleToggleMetricClick = () => {
      const measurementSystem = useViewerStore.getState().measurementSystem;
      useViewerStore.getState().setMeasurementSystem(
         measurementSystem === MeasurementSystem.imperial ? 
         MeasurementSystem.metric : 
         MeasurementSystem.imperial
      );
      setViewMenuOpen(false);
   }

   return (
         <>
            <Toolbar className='mobile-topbar'>
               <Box 
                  sx={{ 
                     display: "flex", 
                     flexDirection: "row", 
                     alignItems: 'center',
                     gap: 1,
                  }}
               >
                  <IconButton data-tour-id="m-menu" onClick={onMenuClick} 
                     sx={{
                        color: 'var(--toolbar-button-border)', 
                        bgcolor: 'var(--toolbar-button-bg)',
                        my: 1,
                        width: 42,
                        height: 42,
                        '&:focus': {
                           bgcolor: 'var(--toolbar-button-bg)',
                        }
                     }}
                  >
                     <svg width="20" height="20"><path fill='currentColor' xmlns="http://www.w3.org/2000/svg" d="m11.666 12.669.135.013a.665.665 0 0 1 0 1.303l-.135.014H3.333a.665.665 0 0 1 0-1.33zm5-6.667.135.013a.665.665 0 0 1 0 1.303l-.135.014H3.333a.665.665 0 0 1 0-1.33z"/></svg>
                  </IconButton>

                  <div
                     className='mobile-topbar-design-name'
                     ref={designNameElRef}
                     tabIndex={0}
                     contentEditable
                     suppressContentEditableWarning
                     onBlur={handleBlur}
                     onKeyDown={(e) => {
                        if (e.key === "Enter") {
                           e.preventDefault();
                           (document.activeElement as HTMLElement)?.blur();
                        }
                     }}
                     enterKeyHint='send'
                  >
                     {localDesignName}
                  </div>
               </Box>

               <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  { 
                     AlignPressureActive &&
                     <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <IconButton
                           onClick={handleDisableActiveActionClick}
                           sx={{ 
                              bgcolor: 'var(--toolbar-button-bg)',
                              color: 'var(--toolbar-button-border)',
                              '&:focus': {
                                 bgcolor: 'var(--toolbar-button-bg)',
                              },
                              '&:hover': {
                                 bgcolor: 'var(--toolbar-button-bg)',
                              } 
                           }}
                        >
                           <GridOnIcon />
                        </IconButton>
                        <Box
                           component="span"
                           sx={{
                              position: 'absolute',
                              top: 1,
                              right: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'var(--toolbar-button-bg)',
                              color: 'var(--toolbar-button-border)',
                              fontSize: 10,
                              lineHeight: 1,
                              pointerEvents: 'none',
                           }}
                        >
                           <CloseIcon sx={{ fontSize: 10 }} />
                        </Box>
                     </Box>
                  }
                  <IconButton
                     ref={viewMenuButtonElRef}
                     data-tour-id="m-view-menu"
                     onClick={handleOpenViewMenu}
                     sx={{ 
                        bgcolor: 'var(--toolbar-button-bg)',
                        '&:focus': {
                           bgcolor: 'var(--toolbar-button-bg)',
                        },
                        '&:hover': {
                           bgcolor: 'var(--toolbar-button-bg)',
                        } 
                     }}
                  >
                     <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" style={{color: 'var(--toolbar-button-border)'}}><g fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3.5"/><path d="M20.188 10.934c.388.472.582.707.582 1.066s-.194.594-.582 1.066C18.768 14.79 15.636 18 12 18s-6.768-3.21-8.188-4.934c-.388-.472-.582-.707-.582-1.066s.194-.594.582-1.066C5.232 9.21 8.364 6 12 6s6.768 3.21 8.188 4.934Z"/></g></svg>
                  </IconButton>
                  <Menu
                     anchorEl={viewMenuButtonElRef.current}
                     anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                     }}
                     transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                     }}
                     keepMounted
                     open={isViewMenuOpen}
                     onClose={handleCloseViewMenu}
                     // Otherwise MUI focuses the first item (Labels) and it looks
                     // selected whenever this menu opens for Tutorial.
                     disableAutoFocusItem
                     sx={{ 
                        mt: 1,
                        '& .MuiList-root': { 
                           backgroundColor: 'var(--toolbar-button-bg)',
                        },
                        '& .MuiMenuItem-root': { color: 'var(--toolbar-button-border)' },
                        '& .MuiPaper-root': {
                           borderRadius: '28px',
                        }
                     }}
                  >
                     <MenuItem data-tour-id="m-labels" onClick={handleToggleLiveModeClick}>
                        {liveModeActive ? (
                           <LabelIcon style={{ color: "var(--accent-color-negative)" }} />
                        ) : (
                           <LabelOffIcon style={{ color: "var(--accent-color-negative)" }} />
                        )}
                     </MenuItem>
                     <MenuItem data-tour-id="m-coloring" onClick={handleToggleColoringClick}><FormatColorFillIcon /></MenuItem>
                     <MenuItem data-tour-id="m-camera" onClick={handleToggleCameraClick}><FilterNoneIcon /></MenuItem>
                     <MenuItem data-tour-id="m-wireframe" onClick={handleCycleWireFrameClick}><BorderOuterIcon /></MenuItem>
                     <MenuItem data-tour-id="m-clipping" onClick={handleToggleClippingClick}><ContentCutIcon /></MenuItem>
                     <MenuItem data-tour-id="m-grid" onClick={handleToggleGridsClick}><GridOnIcon /></MenuItem>
                     <MenuItem data-tour-id="m-feet-view" onClick={handleCycleFeetViewClick}><SplitscreenIcon style={{ transform: 'rotate(90deg)' }} /></MenuItem>
                     <MenuItem data-tour-id="m-measurement" onClick={handleToggleMetricClick}><ChangeCircleIcon style={{ transform: 'rotate(90deg)' }} /></MenuItem>
                     {canUseChatbox && (
                        <MenuItem data-tour-id="m-tutorial" onClick={handleTutorialClick}><SchoolIcon /></MenuItem>
                     )}
                  </Menu>
                  <IconButton
                     ref={editMenuButtonElRef}
                     data-tour-id="m-edit-menu"
                     onClick={handleOpenEditMenu}
                     sx={{ 
                        bgcolor: 'var(--toolbar-button-bg)',
                        '&:focus': {
                           bgcolor: 'var(--toolbar-button-bg)',
                        },
                        '&:hover': {
                           bgcolor: 'var(--toolbar-button-bg)',
                        } 
                     }}
                  >
                     <svg xmlns="http://www.w3.org/2000/svg" style={{color: 'var(--toolbar-button-border)'}} width="24px" height="24px" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="m5 16l-1 4l4-1L19.586 7.414a2 2 0 0 0 0-2.828l-.172-.172a2 2 0 0 0-2.828 0zM15 6l3 3m-5 11h8"/></svg>
                  </IconButton>
                  <Menu
                     anchorEl={editMenuButtonElRef.current}
                     anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                     }}
                     transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                     }}
                     keepMounted
                     open={isEditMenuOpen}
                     onClose={handleCloseEditMenu}
                     sx={{ 
                        mt: 1,
                        '& .MuiList-root': { backgroundColor: 'var(--toolbar-button-bg)' },
                        '& .MuiMenuItem-root': { color: 'var(--toolbar-button-border)' },
                        '& .MuiPaper-root': {
                           borderRadius: '28px',
                        }
                     }}
                  >
                     <MenuItem data-tour-id="m-tab-view" onClick={() => handlePropertyPanelTabClick(ViewerTabsMode.View)}><AutoDesignIcon /></MenuItem>
                     <MenuItem data-tour-id="m-tab-edit" onClick={() => handlePropertyPanelTabClick(ViewerTabsMode.Edit)}><SurfaceEditIcon /></MenuItem>
                     <MenuItem data-tour-id="m-tab-additions" onClick={() => handlePropertyPanelTabClick(ViewerTabsMode.Additions)}><AdditionIcon /></MenuItem>
                     <MenuItem data-tour-id="m-tab-engraving" onClick={() => handlePropertyPanelTabClick(ViewerTabsMode.Engraving)}><EngravingIcon /></MenuItem>
                     <MenuItem data-tour-id="m-tab-project" onClick={() => handlePropertyPanelTabClick(ViewerTabsMode.Project)}><SettingsIcon /></MenuItem>
                  </Menu>
               </Box>
            </Toolbar>

            <SaveTemplatePanel isOpen={isSaveTemplatePanelOpen} onClose={handleSaveTemplateClose} />
         </>
   )

};

export default MobileTopBar;
